import {
  DataTypeStore,
  EntityFieldRelationshipType,
  formatDataObject,
  getOptionsOnFocus,
  IEntityFieldType,
  LayoutElementType,
  LayoutJsonDataType,
  LayoutResponseType,
  SearchCriteria,
  SelectedField,
  fetchTypeStatusForCommunications,
} from '@app-store'
import {
  componentNames,
  DASHBOARD,
  ENTITY_IMPORTANT_KEYS,
  ENTITY_NAME,
  excludedFilterFields,
  FIELD_VALUE_CONSTANTS,
  LAYOUT_TYPE_NAMESPACE,
  NAMESPACES,
  ZIP_CONSTANTS,
} from './constants'
import moment from 'moment'
import {
  EntityLayout,
  LayoutData,
  GetWebEnrollmentStatusOptionsParams,
  EnrollmentStatusCacheRef,
} from '../types/layout.types'
import {
  columnTypesConst,
  EntityLookupSearchCriteria,
  FILTER_OPERATOR_MAP,
  filterOperatorValues,
  filterOptionsConstant,
  FORMAT_PATTERN,
  formatCurrency,
  formatPhoneNumber,
  InitialFilterConfig,
  LookupSearchCriteria,
  MODE_CONSTANTS,
  TableColumnType,
  TableFilterLookupObjectBody,
  TableFilterObjectBody,
  tableFilterType,
} from '@ui-kit'
import { FieldValues } from 'react-hook-form'
import { isNil, isObject } from 'lodash'
import { DYNAMIC_QUEUE_CONSTANTS } from '../constants/dynamicQueue'
import { convertTo24HourFormat } from './time'
import { hasPermission } from './permission'
import {
  getMultiLookUpForListingTable,
  getMultiUploadForListingTable,
} from '../components/listing-table/ListingTableHelperComponents'
import { ListingTableProps } from '../types/listingTable.types'
import { store } from './agent.store'
import { formatLocalDateWithZone } from './date.utils'
import { formatZipCode } from './formatter'
import {
  FILTER_FIELDS,
  NON_FILTER_FIELDS,
} from '../constants/intakeQue.constants'
import { QUEUE_STRINGS } from './queueIntake.types'

const getElementByKey = (layout: LayoutData, key: string) => {
  const pages = layout?.pages
  for (const page of pages) {
    if (!Array.isArray(page.rows)) continue

    for (const row of page.rows) {
      if (!Array.isArray(row.columns)) continue

      for (const column of row.columns) {
        if (!Array.isArray(column.elements)) continue

        for (const element of column.elements) {
          if (element.keyName === key) {
            return element
          }
        }
      }
    }
  }
}

const isZipCodeFieldName = (fieldName: string): boolean => {
  const normalized = fieldName.split('.').pop() ?? fieldName
  return [
    ZIP_CONSTANTS.ZIP_CODE,
    ZIP_CONSTANTS.ZIP_CODE_LOWERCASE,
    ZIP_CONSTANTS.ZIP_CODE_CAMELCASE,
    ZIP_CONSTANTS.GLOBAL_ZIP_CODE,
  ].includes(normalized)
}

export const formatDataForListView = (
  content: DataTypeStore[],
  layout_data?: LayoutData | null | LayoutJsonDataType,
  fromDashboardOrQueue = false
): DataTypeStore[] => {
  return content.map((record) => {
    const layoutFormat = layout_data?.added_fields || {}
    const keys = Object.keys(record)
    keys.forEach((col) => {
      const val = record[col]
      if (isNil(val)) return

      if (typeof val === 'boolean' && col === 'selected') return // for 2 level relationships in 360

      if (typeof val === 'object' && !Array.isArray(val) && val !== null) {
        const selected_fields =
          (layoutFormat?.[col]?.selected_fields as SelectedField[]) ??
          Object.keys(val)
            .filter((key) => key !== 'id')
            .map((key) => {
              return { name: key, id: 0, label: key }
            }) // as we would need layout to get selected fields - we just use whatever we get from api for now.
        record[col] = generateLabelFromSelectedFields(
          selected_fields,
          val,
          layoutFormat?.[col]?.relationship?.target_entity_id ?? ''
        ) // for single lookup show name attrib
        if (layoutFormat?.[col]?.ui_data_type === componentNames.FILE_UPLOAD) {
          record[col] = val?.['file_name' as keyof typeof val]
        }
        if (layoutFormat?.[col]?.ui_data_type === componentNames.CATEGORY) {
          record[col] = val?.['value' as keyof typeof val]
        } else
          for (const lookupKey in val as Record<string, string>)
            record[`${col}.${lookupKey}`] = formatValue(
              (val as DataTypeStore)[lookupKey],
              layoutFormat[col]?.added_fields?.[lookupKey]?.ui_data_type ?? ''
            )
      }

      if (layout_data && col in layoutFormat) {
        const element = getElementByKey(
          layout_data as unknown as LayoutData,
          col
        )
        const formattedVal = formatValue(
          record[col],
          layoutFormat?.[col]?.ui_data_type ?? '',
          fromDashboardOrQueue,
          element?.overrideBoolean ?? false
        )
        record[col] = formattedVal
      }

      if (
        isZipCodeFieldName(col) &&
        (typeof record[col] === 'string' || typeof record[col] === 'number')
      ) {
        record[col] = formatZipCode(String(record[col]))
      }
    })
    return record
  })
}

const formatValue = (
  val: string | string[] | number | boolean | number[],
  dataType: string,
  _fromDashboardOrQueue?: boolean,
  overrideBoolean = true
) => {
  if (isNil(val) || val === '') return val

  switch (dataType) {
    case componentNames.TIME: {
      const currentDateString = new Date().toISOString().split('T')[0]
      const fullDateTime = `${currentDateString}T${
        val.toString().includes('Z') ? val : val + 'Z'
      }`
      const date = new Date(fullDateTime)
      const timezoneOffset = date.getTimezoneOffset()
      const value = new Date(date.getTime() - timezoneOffset * 60000)
        .toISOString()
        .split('.')[0]
        .split('T')[1]
      return convertTo24HourFormat(value)
    }
    case componentNames.MULTI_SELECT_DROPDOWN:
      return (val as string[])?.join(', ')

    case componentNames.DATE_TIME: {
      const formatDateTime = formatLocalDateWithZone(val as string)
      return formatDateTime
    }

    case componentNames.DATE:
      return moment(val as string)?.format('MM/DD/YYYY')

    case componentNames.PHONE_NUMBER:
    case componentNames.FAX_NUMBER:
      return formatPhoneNumber(val as string, FORMAT_PATTERN.PHONE_INPUT)

    case componentNames.ZIP_CODE:
      return formatZipCode(val as string)

    case componentNames.CURRENCY:
      return formatCurrency(val as string) as string

    case componentNames.CHECKBOX:
      if (!overrideBoolean) {
        return (!!val).toString()
      } else {
        return val ? 'Yes' : 'No'
      }
    case componentNames.CATEGORY:
      if (isObject(val)) {
        return 'value' in val
          ? (val as { value: string | string[] | number | boolean | number[] })
              ?.value ?? ''
          : '--'
      }
      return val
    default:
      return val
  }
}

export const generateLabelFromSelectedFields = (
  selected_fields: SelectedField[],
  data: DataTypeStore,
  lookupEntityId: string | number
) => {
  if (!data || !selected_fields) return '--'
  const labelVal = selected_fields
    ?.sort((a, b) => {
      return (a.field_order ?? 0) - (b.field_order ?? 0)
    })
    .map(({ name, ui_data_type, id }) => {
      return (
        formatValue(data[name], ui_data_type ?? '') ??
        (hasPermission(MODE_CONSTANTS.VIEW, lookupEntityId ?? '', id)
          ? '--'
          : NAMESPACES.REDACTED)
      )
    })
    .map((val) => val || '--')
    .join(' | ')
  return labelVal.length === FIELD_VALUE_CONSTANTS.ZERO ? '--' : labelVal
}

export const formatFilterArrayFields = (
  filterObject: TableFilterObjectBody
) => {
  return {
    fields: filterObject?.fields ?? null,
    search_criteria: Object.values(filterObject?.search_criteria || {}),
  }
}

export const formatFilterArray = (
  filterObject: TableFilterObjectBody | undefined
) => {
  const values = Object.values(filterObject?.search_criteria || {})
  const keys = Object.keys(filterObject?.search_criteria || {})
  const formattedData: formatDataObject[] = []
  if (values?.length) {
    values?.forEach((val, index) => {
      const newObj: formatDataObject = {
        values: [],
        operation: '',
        field_name: '',
      }
      newObj.values = val?.search_value || []
      newObj.operation = val?.operator || ''
      newObj.field_name = keys[index] || ''
      formattedData.push(newObj)
    })
  }

  return {
    logical_operator: filterObject?.fields ?? 'AND',
    criteria: formattedData,
  }
}

const mapFilterOperator = (
  operator: string | undefined,
  column?: TableColumnType<DataTypeStore>
): string => {
  if (!operator) return ''
  if (
    column?.filterData?.dataType === tableFilterType.DATE_TIME_DYNAMIC ||
    column?.componentName === componentNames.DATE_TIME
  ) {
    return operator
  }
  return FILTER_OPERATOR_MAP[operator] ?? operator
}

export const formatFilterConfig = (
  filterObject: TableFilterObjectBody,
  columnData: TableColumnType<DataTypeStore>[],
  filterFormat: 'Queue' | 'Entity',
  sortConfig?: { key: string; direction: string }
) => {
  const searchCriteria = filterObject?.search_criteria || {}
  const formatFilters: LookupSearchCriteria[] = []
  const formatEntityFilters: EntityLookupSearchCriteria[] = []
  const formattedLookupFilters: TableFilterLookupObjectBody = {}

  Object.entries(searchCriteria).forEach(([key, values]) => {
    if (!isNaN(parseFloat(key))) key = values.search_key
    const column = columnData.find((item) => item?.accessor === key)

    if (column?.isLookup && column.LookupConfig) {
      const lookupValues: { [lookupKey: string]: string[] } = {}
      column.LookupConfig.forEach((lookupKey) => {
        lookupValues[lookupKey] = []
      })

      values?.search_value.forEach((value) => {
        const splitValues = value.split('|')
        splitValues.forEach((splitValue, index) => {
          if (column?.LookupConfig?.[index] && splitValue?.trim()?.length > 0) {
            lookupValues[column?.LookupConfig[index]].push(splitValue?.trim())
          } else if (
            column?.LookupConfig &&
            index >= column?.LookupConfig?.length
          ) {
            const lastLookupKey =
              column?.LookupConfig[column?.LookupConfig.length - 1]
            lookupValues[lastLookupKey].push(splitValue?.trim())
          }
        })
      })

      formattedLookupFilters[key] = {
        select: column?.lookupAddedFields ?? [],
        filters: [
          ...(formattedLookupFilters[key]?.filters ?? []),
          {
            logical_operator:
              values?.operator === filterOperatorValues.IS_EMPTY_NULL
                ? 'AND'
                : 'OR',
            criteria: column.LookupConfig.map((lookupKey, index) => ({
              field_name: lookupKey,
              values:
                values?.operator === filterOperatorValues.IS_EMPTY_NULL ||
                values?.operator === filterOperatorValues.IS_NOT_EMPTY_NOT_NULL
                  ? ['']
                  : lookupValues[lookupKey] ?? [],
              operation: mapFilterOperator(values.operator, column),
              ...(index === 0 && sortConfig?.key === key
                ? { direction: sortConfig?.direction }
                : {}),
            })),
          },
        ],
      }
    } else if (!column?.isLookup && filterFormat === 'Queue') {
      formatFilters.push({
        field_name: key,
        values: values?.search_value || [],
        operation: mapFilterOperator(values?.operator, column),
        sort_direction:
          sortConfig?.key === key ? sortConfig?.direction : undefined,
      })
    } else if (!column?.isLookup && filterFormat === 'Entity') {
      formatEntityFilters.push({
        criteria: [
          {
            field_name: key,
            values: values?.search_value || [],
            operation: mapFilterOperator(values?.operator, column),
            direction:
              sortConfig?.key === key ? sortConfig?.direction : undefined,
          },
        ],
      })
    }
  })

  // Handling filter response when sortConfig.key is not present in searchCriteria
  if (
    sortConfig?.key &&
    !Object.keys(searchCriteria).some(
      (criteriaKey) => criteriaKey === sortConfig.key
    ) &&
    !Object.values(searchCriteria).some(
      (criteria) => criteria.search_key === sortConfig.key
    )
  ) {
    const column = columnData.find((item) => item?.accessor === sortConfig.key)
    if (column) {
      if (column?.isLookup && column.LookupConfig) {
        formattedLookupFilters[sortConfig.key] = {
          select: column?.lookupAddedFields ?? [],
          filters: [
            ...(formattedLookupFilters[sortConfig.key]?.filters ?? []),
            {
              logical_operator: 'OR',
              criteria: column.LookupConfig.map((lookupKey, index) => ({
                field_name: lookupKey,
                values: [],
                operation: filterOptionsConstant['string'][0].value,
                ...(index === 0 ? { direction: sortConfig?.direction } : {}),
              })),
            },
          ],
        }
      } else if (!column?.isLookup && filterFormat === 'Queue') {
        formatFilters.push({
          field_name: sortConfig.key,
          values: [],
          operation: 'CONTAINS',
          sort_direction: sortConfig?.direction,
        })
      } else if (!column?.isLookup && filterFormat === 'Entity') {
        formatEntityFilters.push({
          criteria: [
            {
              field_name: sortConfig.key,
              values: [],
              operation: 'CONTAINS',
              direction: sortConfig?.direction,
            },
          ],
        })
      }
    }
  }

  //Handling configuration of that lookup field, who further contains some added_fields but there is no filter associated to it
  columnData.forEach((column) => {
    if (
      column?.isLookup &&
      column.lookupAddedFields &&
      column.lookupAddedFields.length > 0 &&
      !formattedLookupFilters[column.accessor]
    ) {
      formattedLookupFilters[column.accessor] = {
        select: column.lookupAddedFields,
        filters: [],
      }
    }
  })

  if (filterFormat === 'Queue') {
    return {
      filter_criteria: formatFilters,
      related_entities: formattedLookupFilters,
    }
  }

  return {
    filter_criteria: formatEntityFilters,
    related_entities: formattedLookupFilters,
  }
}

export const formatFilterConfigNew = (
  filterObject: TableFilterObjectBody | undefined,
  columnData: TableColumnType<DataTypeStore>[]
) => {
  const searchCriteria = filterObject?.search_criteria || {}
  const formatFilters: LookupSearchCriteria[] = []
  const formattedLookupFilters: TableFilterLookupObjectBody = {}

  Object.entries(searchCriteria).forEach(([key, values]) => {
    const column = columnData.find((item) => item?.accessor === key)

    if (column?.isLookup && column.LookupConfig) {
      const lookupValues: { [lookupKey: string]: string[] } = {}
      column.LookupConfig.forEach((lookupKey) => {
        lookupValues[lookupKey] = []
      })

      values?.search_value.forEach((value) => {
        const splitValues = value.split('|')
        splitValues.forEach((splitValue, index) => {
          if (column?.LookupConfig?.[index] && splitValue?.trim()?.length > 0) {
            lookupValues[column?.LookupConfig[index]].push(splitValue)
          }
        })
      })

      formattedLookupFilters[key] = {
        filters: [
          {
            criteria: column.LookupConfig.map((lookupKey) => ({
              field_name: lookupKey,
              values: lookupValues[lookupKey] || [],
              operation: mapFilterOperator(values.operator, column),
            })),
          },
        ],
      }
    } else {
      formatFilters.push({
        field_name: key,
        values: values?.search_value || [],
        operation: mapFilterOperator(values?.operator, column),
      })
    }
  })

  return {
    filter_criteria: formatFilters,
    related_entities: formattedLookupFilters,
  }
}

export const formatFilterArrayQueue = (filterObject: TableFilterObjectBody) => {
  return {
    filter_criteria: Object.values(filterObject?.search_criteria || {}),
  }
}

export const getTableHeadersFromLayout = (
  layout: EntityLayout | undefined,
  entityName = ''
): TableColumnType<DataTypeStore>[] => {
  const tableHeader: TableColumnType<DataTypeStore>[] =
    layout?.layout_data?.pages?.[0].rows?.[0].columns?.[0].elements.map(
      (element) => {
        const addedField =
          layout?.layout_data?.added_fields?.[element?.keyName ?? '']
        const uiDataType = addedField?.ui_data_type

        const keyAccessor = element.parent?.lookupKeyName
          ? `${element.parent.lookupKeyName}.${element.keyName}`
          : element.keyName ?? ''
        const layoutFix = Object.keys(addedField?.added_fields ?? {})

        return {
          header: addedField?.label ?? element.meta?.label ?? '',
          accessor: keyAccessor,
          type:
            (element.keyName === QUEUE_STRINGS.COMMUNICATION_ID &&
              entityName === ENTITY_NAME.COMMUNICATION) ||
            element.keyName === QUEUE_STRINGS.INTAKE_ID_KEY
              ? 'hyperlink'
              : '',
          minWidth: '3',
          isSortable: !element?.parent,
          filterData: {
            dataType: getTableFilterTypeFromComponentName(uiDataType ?? ''),
            convertToUppercase: false,
            format:
              keyAccessor === 'sender_name'
                ? FORMAT_PATTERN.PHONE_INPUT_2
                : undefined,
          },
          isLookup:
            uiDataType?.includes(componentNames.CATEGORY) ||
            uiDataType?.includes(componentNames.SINGLE_LOOKUP) ||
            false,
          LookupConfig:
            addedField?.selected_fields
              ?.sort((a, b) => {
                return (
                  parseInt(a.field_order ?? '0') -
                  parseInt(b.field_order ?? '0')
                )
              })
              ?.map((field) => field?.field_name ?? '') ?? [],
          lookupAddedFields: layoutFix ?? [],
        }
      }
    ) || []

  tableHeader?.unshift({
    header: DYNAMIC_QUEUE_CONSTANTS.CHECKBOX,
    accessor: DYNAMIC_QUEUE_CONSTANTS.SELECTED,
    type: DYNAMIC_QUEUE_CONSTANTS.CHECKBOX,
    isVisibleInHeader: true,
    isEnabledOnDisabledRow: true,
  })

  return tableHeader ?? []
}

export const getTableHeadersInQueueCreation = (
  layout: EntityLayout | undefined,
  entityName = '',
  disabledFilters?: { key: string; disabled: boolean }[]
): TableColumnType<DataTypeStore>[] => {
  const tableHeader: TableColumnType<DataTypeStore>[] =
    layout?.layout_data?.pages?.[0].rows?.[0].columns?.[0].elements.map(
      (element) => {
        const uiDataType =
          layout?.layout_data?.added_fields?.[element?.keyName ?? '']
            ?.ui_data_type

        const keyAccessor = element.parent?.lookupKeyName
          ? `${element.parent.lookupKeyName}.${element.keyName}`
          : element.keyName ?? ''

        const addedField =
          layout?.layout_data?.added_fields?.[element?.keyName ?? '']

        const layoutFix = Object.keys(addedField?.added_fields ?? {})

        return {
          header:
            layout?.layout_data?.added_fields?.[element?.keyName ?? '']
              ?.label ??
            element.meta?.label ??
            '',
          accessor: keyAccessor,
          type:
            element.keyName === 'communication_id' &&
            entityName === ENTITY_NAME.COMMUNICATION
              ? 'hyperlink'
              : '',
          minWidth: '3',
          isSortable: !element?.parent,
          filterData:
            element?.elementName !== componentNames.SINGLE_LOOKUP
              ? getFilterData(
                  element,
                  keyAccessor === 'sender_name',
                  layout?.layout_data?.added_fields?.[element?.keyName ?? '']
                )
              : {
                  dataType: tableFilterType.STRING_DYNAMIC,
                  convertToUppercase: false,
                },
          isLookup:
            uiDataType?.includes(componentNames.CATEGORY) ||
            uiDataType?.includes(componentNames.SINGLE_LOOKUP),
          LookupConfig: element?.elementName?.includes(componentNames.CATEGORY)
            ? ['value']
            : layout?.layout_data?.added_fields?.[
                element?.keyName ?? ''
              ]?.selected_fields?.map((field) => field?.field_name ?? '') ?? [],
          isFilterDisabled:
            disabledFilters?.find(
              (item) =>
                item?.key ===
                (element.parent?.lookupKeyName
                  ? `${element.parent.lookupKeyName}.${element.keyName}`
                  : element.keyName ?? '')
            )?.disabled ?? false,
          lookupAddedFields: layoutFix ?? [],
        }
      }
    ) || []
  return tableHeader ?? []
}

export const removeUnnecessaryKeys = (keyPrefix: string, obj: FieldValues) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key, _]) => !key.startsWith(keyPrefix))
  )
}
type TableFilterType = (typeof tableFilterType)[keyof typeof tableFilterType]

export const getTableFilterTypeFromComponentName = (
  name: string
): TableFilterType => {
  const dropdowns = [
    componentNames.DROPDOWN,
    componentNames.RADIO_GROUP,
    componentNames.CHECKBOX,
    componentNames.CATEGORY,
  ] as string[]
  if (dropdowns.includes(name)) return tableFilterType.DROPDOWN
  if (name === componentNames.MULTI_SELECT_DROPDOWN)
    return tableFilterType.MULTI_SELECT_DYNAMIC
  if (Object.values(tableFilterType).includes(name as TableFilterType)) {
    if (name === tableFilterType.DATE_TIME)
      return tableFilterType.DATE_TIME_DYNAMIC
    return name as TableFilterType
  }
  return tableFilterType.STRING_DYNAMIC
}

const getColType = (
  header: LayoutElementType,
  entityId: number,
  preventRedirect?: boolean
): (typeof columnTypesConst)[keyof typeof columnTypesConst] => {
  if (!hasPermission(MODE_CONSTANTS.VIEW, entityId, header.id ?? ''))
    return columnTypesConst.TEXT

  const type = header?.elementName?.toString() ?? ''
  if (
    type === componentNames.FILE_UPLOAD ||
    type === componentNames?.MULTI_LOOKUP
  )
    return columnTypesConst.COMPONENT
  if (type === componentNames.UNIQUE_ID && header.parent)
    return columnTypesConst.TEXT

  const hyperlinkTypes = [
    componentNames.URL,
    componentNames.WEBSITE,
    componentNames.UNIQUE_ID,
    componentNames.EMAIL,
    componentNames.FILE_UPLOAD,
    componentNames.SINGLE_LOOKUP_CLICK,
  ] as string[]

  if (
    hyperlinkTypes.includes(type) ||
    header?.isCallable ||
    header?.keyName === ENTITY_IMPORTANT_KEYS.CASE_ID ||
    header?.keyName === ENTITY_IMPORTANT_KEYS.TASK_ID
  ) {
    if (preventRedirect) return columnTypesConst.TEXT
    return columnTypesConst.HYPERLINK
  }

  return columnTypesConst.TEXT
}

export const transformLayoutToCols = (
  layout: LayoutResponseType,
  entityId: number,
  entityName: string,
  preventRedirect?: boolean,
  dataId?: string,
  relationIdentifier?: string,
  getStringFilterOptions?: (
    searchCriteria: SearchCriteria,
    targetEntityId?: number,
    fieldName?: string
  ) => Promise<string[]>,
  isSortable = true,
  disableOnFocusForColumn = false
) => {
  const keysToExclude = ['id']
  let colsInd = -1

  return (
    (layout.layout_data?.pages?.[0].rows?.[0].columns?.[0]?.elements ?? [])
      .map((col) => {
        const accessor = col.parent?.lookupKeyName
          ? `${col.parent.lookupKeyName}.${col.keyName}`
          : col.keyName ?? ''

        return {
          header: col.meta?.label ?? '',
          accessor,
          type: getColType(
            col,
            +(col.parent?.entityId ?? entityId),
            preventRedirect
          ),
          componentName: col.elementName ?? '',
          minWidth: '6',
          isSortable:
            col.elementName !== componentNames.FILE_UPLOAD &&
            col.elementName !== componentNames.MULTI_LOOKUP &&
            isSortable,
          filterData: buildFilterData(
            entityName ?? '',
            col,
            accessor === 'sender_name',
            getStringFilterOptions
              ? (
                  searchCriteria: SearchCriteria,
                  page: number,
                  size: number,
                  targetEntityId?: number,
                  fieldName?: string
                ) => {
                  const isFilterAllowed = checkFilterColumn(
                    entityName ?? '',
                    searchCriteria.search_key ?? ''
                  )

                  return isFilterAllowed
                    ? getStringOptionsOnFocus(
                        searchCriteria,
                        page,
                        size,
                        targetEntityId,
                        fieldName,
                        entityName,
                        dataId,
                        relationIdentifier
                      )
                    : Promise.resolve({ data: [], size: 0 })
                }
              : async () => ({ data: [], size: 0 }),
            col?.elementName !== componentNames.SINGLE_LOOKUP
              ? getStringFilterOptions
              : undefined,
            col?.overrideBoolean,
            layout.layout_data?.added_fields?.[accessor],
            undefined,
            disableOnFocusForColumn
          ),
          isLookup:
            col.elementName?.includes(componentNames.CATEGORY) ||
            col.elementName?.includes(componentNames.SINGLE_LOOKUP) ||
            false,
          LookupConfig: col.elementName?.includes(componentNames.CATEGORY)
            ? ['value']
            : layout.layout_data?.added_fields?.[
                accessor
              ]?.selected_fields?.map((field) => field?.field_name ?? '') ?? [],
          is_hidden: col.is_hidden ?? false,
        }
      })
      //NOTE : removed is_hidden filter from here as we want to fetch data for hidden fields, REMOVED piece of code && !col.is_hidden
      .filter((col) => !keysToExclude.includes(col.accessor))
      .map((col) => {
        colsInd += 1
        if (
          col.componentName === componentNames.MULTI_LOOKUP &&
          col.type === columnTypesConst.COMPONENT
        ) {
          return {
            renderComponent: getMultiLookUpForListingTable(
              layout?.layout_data?.added_fields?.[col?.accessor]
                ?.relationship as EntityFieldRelationshipType,
              entityName,
              col.accessor,
              layout,
              colsInd === 0
            ),
            ...col,
          }
        } else if (
          col.componentName === componentNames.FILE_UPLOAD &&
          col.type === columnTypesConst.COMPONENT
        )
          return {
            renderComponent: getMultiUploadForListingTable(
              entityName,
              col.accessor,
              layout,
              colsInd === 0
            ),
            ...col,
          }
        else
          return {
            renderComponent: undefined,
            ...col,
          }
      })
  )
}

export const getStringOptionsOnFocus = async (
  searchCriteria: SearchCriteria,
  page: number,
  size: number,
  targetEntityId?: number,
  fieldName?: string,
  entityName?: string,
  dataId?: string,
  relationIdentifier?: string
) => {
  try {
    if (!checkFilterColumn(entityName ?? '', searchCriteria.search_key ?? ''))
      return { data: [], size: 0 }
    const payload = {
      entity_id: targetEntityId,
      select: [fieldName],
      distinct: true,
      filters: [
        {
          criteria: [
            {
              field_name: fieldName,
              operation: searchCriteria.operator,
              values: searchCriteria.search_value,
              direction: 'ASC',
            },
          ],
        },
      ],
      page: page - 1,
      size: size,
      ...(relationIdentifier && { relation_identifier: relationIdentifier }),
    }
    const response = await getOptionsOnFocus(payload, dataId)
    return {
      data: response?.data?.content
        ?.filter((item: { [key: string]: string }) => {
          const value = item[fieldName ?? '']
          return value !== null && value.trim() !== ''
        })
        .map((item: { [key: string]: string }) => item[fieldName ?? '']),
      size: response?.data?.content?.length,
    }
  } catch (err) {
    console.error(err)
    return { data: [], size: 0 }
  }
}

export const wrappedGetStringOptionsForEntities = async (
  searchCriteria: SearchCriteria,
  targetEntityId?: number,
  fieldName?: string,
  entityName?: string,
  dataId?: string,
  relationIdentifier?: string
) => {
  const response = await getStringOptionsOnFocus(
    searchCriteria,
    1,
    10,
    targetEntityId,
    fieldName,
    entityName,
    dataId,
    relationIdentifier
  )
  return response?.data ?? []
}
export const enabledForSpecificLayout = (
  layoutName: string,
  keyName: string
) => {
  if (
    (layoutName === DASHBOARD.AE_PC_DASHBOARD ||
      layoutName === LAYOUT_TYPE_NAMESPACE.LAYOUT_LISTING_LABEL) &&
    keyName === 'overdue_time'
  ) {
    return false
  } else if (
    layoutName === DASHBOARD.CASE_DASHBOARD &&
    keyName === 'overall_case_status'
  ) {
    return false
  }

  return true
}

export const getFilterData = (
  col: LayoutElementType,
  isValid: boolean,
  field?:
    | (IEntityFieldType & { filter_criteria?: { category_type?: string } })
    | undefined
    | null,
  getStringFilterOptions?: (
    searchCriteria: SearchCriteria,
    targetEntityId?: number,
    fieldName?: string
  ) => Promise<string[]>,
  overrideBoolean = false,
  onFocus?: (
    searchCriteria: SearchCriteria,
    page: number,
    size: number
  ) => Promise<{ data: string[]; size: number }>,
  layoutName?: string
): NonNullable<ListingTableProps['extraColumns']>[0]['filterData'] => {
  const type = col?.elementName
  if (!type) return undefined
  if (
    excludedFilterFields.includes(type) ||
    (col.parent &&
      col.keyName &&
      enabledForSpecificLayout(layoutName ?? '', col.keyName))
  )
    return undefined

  const getFilterOptions = () => {
    if (type === componentNames.CATEGORY)
      return (
        store.getState()?.categoryDropdown?.categoryMap[
          field?.filter_criteria?.category_type ?? ''
        ] ?? []
      ).map((val) => ({
        value: val.value.toString(),
        label: val.value,
      }))

    if (type === componentNames.CHECKBOX)
      if (!overrideBoolean) {
        return [true, false].map((val) => ({
          label: val.toString(),
          value: val.toString(),
        }))
      } else {
        return [
          { label: 'Yes', value: 'true' },
          { label: 'No', value: 'false' },
        ]
      }
    return col.meta?.options
  }
  const options = getFilterOptions()

  const dataType = getTableFilterTypeFromComponentName(type)
  // if (type === componentNames.CATEGORY && !options?.length)
  //   dataType = tableFilterType.STRING_DYNAMIC

  let format: string | undefined = undefined
  if (
    type === componentNames.PHONE_NUMBER ||
    type === componentNames.FAX_NUMBER
  ) {
    format = FORMAT_PATTERN.PHONE_INPUT
  } else if (isValid) {
    format = FORMAT_PATTERN.PHONE_INPUT_2
  }
  return {
    dataType: dataType,
    convertToUppercase: false,
    dropDownOptions: options,
    format: format,
    onKeyPress: getStringFilterOptions,
    onFocus: onFocus,
  }
}

export const getWebEnrollmentStatusFilterOptions = async ({
  searchTerm = '',
  page,
  size,
  cacheRef,
  categoryType = NAMESPACES.WEB_ENROLLMENT_STATUS,
}: GetWebEnrollmentStatusOptionsParams): Promise<{
  data: string[]
  size: number
}> => {
  const search = searchTerm.trim().toLowerCase()
  if (!cacheRef.current) {
    const statusResponse = await fetchTypeStatusForCommunications(categoryType)
    cacheRef.current =
      statusResponse?.data
        ?.filter((item: { value?: string }) => item.value?.trim())
        .map((item: { value: string }) => item.value) ?? []
  }

  const currentCache = cacheRef.current ?? []

  const allFiltered = currentCache.filter((value) => {
    const val = value.toLowerCase()
    return search === '' || val.includes(search)
  })
  const start = (page - 1) * size
  const pageData = allFiltered.slice(start, start + size)
  return {
    data: pageData,
    size: pageData.length,
  }
}

export const clearWebEnrollmentStatusCache = (
  cacheRef: EnrollmentStatusCacheRef
) => {
  cacheRef.current = null
}

export const isWithinLast24Hours = (isoDateStr: string): boolean => {
  const inputDate = moment(isoDateStr)
  const now = moment()

  return now.diff(inputDate, 'hours') <= 24 && now.isAfter(inputDate)
}

export const removeColumns = (
  data: TableColumnType<DataTypeStore>[],
  columnsToRemove: string[]
) => {
  return data.filter(
    (item) => !columnsToRemove?.includes(String(item.accessor))
  )
}

export const normalizeInitialFilters = (
  initialFilters: InitialFilterConfig | undefined
): InitialFilterConfig | null => {
  if (initialFilters === null) return null
  let parsed: unknown = initialFilters
  if (typeof initialFilters === 'string') {
    try {
      parsed = JSON.parse(initialFilters)
    } catch {
      return null
    }
  }
  if (typeof parsed !== 'object' || Array.isArray(parsed)) return null
  const obj = parsed as Record<string, unknown>
  if (obj && ('related_entities' in obj || 'filters' in obj)) {
    const config: InitialFilterConfig = {
      related_entities:
        (obj.related_entities as TableFilterLookupObjectBody) ?? {},
      ...(obj.filters
        ? { filters: obj.filters as EntityLookupSearchCriteria }
        : {}),
    }
    const isEmpty =
      !config.filters?.criteria?.length &&
      Object.keys(config.related_entities ?? {}).length === 0
    return isEmpty ? null : config
  }
  return obj
}
export const mergeInitialFilterCriteria = (
  baseFilters: EntityLookupSearchCriteria[],
  initialFilters?: EntityLookupSearchCriteria
): EntityLookupSearchCriteria[] => {
  if (!initialFilters?.criteria?.length) {
    return baseFilters
  }
  return [initialFilters, ...baseFilters]
}

export const mergeInitialFilters = (
  base: TableFilterLookupObjectBody,
  initialFilters: TableFilterLookupObjectBody
): TableFilterLookupObjectBody => {
  const merged = structuredClone(base)
  Object.entries(initialFilters).forEach(([fieldKey, initialEntity]) => {
    const existing = merged[fieldKey]
    if (!existing) {
      merged[fieldKey] = structuredClone(initialEntity)
      return
    }
    merged[fieldKey] = {
      ...existing,
      ...initialEntity,
      select: existing.select ?? initialEntity.select,
      filters: [...(existing.filters ?? []), ...(initialEntity.filters ?? [])],
    }
  })
  return merged
}
export const buildRelatedEntitiesWithFilters = (
  selectOnly: Record<string, { select: string[] }>,
  withFilters: TableFilterLookupObjectBody = {}
): TableFilterLookupObjectBody => {
  const result: TableFilterLookupObjectBody = structuredClone(withFilters)
  Object.entries(selectOnly).forEach(([key, value]) => {
    result[key] = {
      select: value.select,
      filters: result[key]?.filters ?? [],
    }
  })
  return result
}

export const applyInitialFilterConfig = (args: {
  runtimeFilters: EntityLookupSearchCriteria[]
  runtimeRelatedEntities: TableFilterLookupObjectBody
  initialConfig: InitialFilterConfig | null
}): {
  filters: EntityLookupSearchCriteria[]
  related_entities: TableFilterLookupObjectBody
} => {
  const { runtimeFilters, runtimeRelatedEntities, initialConfig } = args
  if (!initialConfig) {
    return {
      filters: runtimeFilters,
      related_entities: runtimeRelatedEntities,
    }
  }
  return {
    filters: mergeInitialFilterCriteria(runtimeFilters, initialConfig?.filters),
    related_entities: mergeInitialFilters(
      runtimeRelatedEntities,
      initialConfig.related_entities ?? {}
    ),
  }
}

export const buildFilterData = (
  entityName: string,
  element: LayoutElementType,
  isValid: boolean,
  getStringOptions: (
    searchCriteria: SearchCriteria,
    page: number,
    size: number,
    targetEntityId?: number,
    fieldName?: string,
    isSingleLookup?: boolean
  ) => Promise<{ data: string[]; size: number }>,
  getStringFilterOptions?: (
    searchCriteria: SearchCriteria,
    targetEntityId?: number,
    fieldName?: string,
    isSingleLookup?: boolean
  ) => Promise<string[]>,
  overrideBoolean?: boolean,
  field?:
    | (IEntityFieldType & { filter_criteria?: { category_type?: string } })
    | undefined
    | null,
  layoutName?: string,
  disableOnFocusForColumn?: boolean,
  isDashboard?: boolean
) => {
  const isSingleLookup = element.elementName === componentNames.SINGLE_LOOKUP

  const targetEntityId = isSingleLookup
    ? field?.relationship?.target_entity_id
    : field?.d_entity_id

  const fieldName = isSingleLookup
    ? field?.selected_fields?.[0]?.field_name
    : field?.field_name

  const filterType = getTableFilterTypeFromComponentName(element?.keyName ?? '')
  const isStringFilter =
    filterType === tableFilterType.STRING ||
    filterType === tableFilterType.STRING_DYNAMIC

  return getFilterData(
    element,
    isValid,
    field,
    (searchCriteria: SearchCriteria) =>
      getStringFilterOptions?.(
        searchCriteria,
        targetEntityId,
        fieldName,
        isSingleLookup
      ) ?? Promise.resolve([]),
    overrideBoolean,
    isStringFilter &&
      targetEntityId &&
      checkFilterColumn(entityName ?? '', element.keyName ?? '') &&
      !(
        checkOnFocusColumn(entityName ?? '', element.keyName ?? '') &&
        disableOnFocusForColumn
      ) &&
      !isDashboard
      ? (searchCriteria: SearchCriteria, page: number, size: number) =>
          getStringOptions(
            searchCriteria,
            page,
            size,
            targetEntityId,
            fieldName,
            isSingleLookup
          )
      : undefined,
    layoutName
  )
}

export const checkFilterColumn = (
  entity_name: string,
  fieldName: string
): boolean => {
  const fields = FILTER_FIELDS[entity_name as keyof typeof FILTER_FIELDS]
  return fields?.includes(fieldName)
}

export const checkOnFocusColumn = (
  entity_name: string,
  fieldName: string
): boolean => {
  const fields =
    NON_FILTER_FIELDS[entity_name as keyof typeof NON_FILTER_FIELDS]
  return fields?.includes(fieldName)
}
